import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, Grid, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TextField, Typography,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { loanApi } from '../../../api/loanApi';
import {
  isNeverDisbursed, LoanDetailDto, LoanInstallmentStatus, LoanInstallmentStatusLabels,
  LoanInterestMethodLabels, LoanStatus, LoanStatusColors, LoanStatusLabels,
  LoanTransactionTypeLabels, LoanTypeLabels,
} from '../../../types/loanTypes';

const money = (value: number) =>
  `₹${(value ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const fmtDate = (v?: string | null) => (v ? new Date(v).toLocaleDateString('en-IN') : '—');

type ActionKind =
  | 'approve' | 'reject' | 'cancel' | 'disburse' | 'repayment' | 'foreclose'
  | 'writeoff' | 'skip' | 'waive';

interface ActionState {
  kind: ActionKind;
  installmentId?: string;
  title: string;
  needsAmount?: boolean;
  needsReference?: boolean;
}

const Field: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Grid size={{ xs: 6, md: 3 }}>
    <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
    <Typography variant="body2" fontWeight={600}>{value}</Typography>
  </Grid>
);

const LoanDetailPage: React.FC<{ selfService?: boolean }> = ({ selfService = false }) => {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const [loan, setLoan] = useState<LoanDetailDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState<ActionState | null>(null);
  const [remarks, setRemarks] = useState('');
  const [amount, setAmount] = useState('');
  const [reference, setReference] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = selfService ? await loanApi.getMyLoan(id) : await loanApi.getLoan(id);
      setLoan(data);
    } catch {
      toast.error('Failed to load loan');
    } finally {
      setLoading(false);
    }
  }, [id, selfService]);

  useEffect(() => { void load(); }, [load]);

  const openAction = (state: ActionState) => {
    setRemarks('');
    setAmount('');
    setReference('');
    setAction(state);
  };

  const amountExceedsOutstanding =
    action?.kind === 'repayment' && Number(amount) > (loan?.outstandingBalance ?? 0);

  const runAction = async () => {
    if (!action || !loan) return;
    setBusy(true);
    try {
      switch (action.kind) {
        case 'approve': await loanApi.approveLoan(loan.id, remarks); break;
        case 'reject': await loanApi.rejectLoan(loan.id, remarks); break;
        case 'cancel': await loanApi.cancelLoan(loan.id, remarks); break;
        case 'disburse': await loanApi.disburseLoan(loan.id, { reference: reference || undefined }); break;
        case 'repayment':
          await loanApi.recordRepayment(loan.id, { amount: Number(amount), reference: reference || undefined, remarks: remarks || undefined });
          break;
        case 'foreclose': await loanApi.forecloseLoan(loan.id, { reference: reference || undefined, remarks: remarks || undefined }); break;
        case 'writeoff': await loanApi.writeOffLoan(loan.id, remarks); break;
        case 'skip': await loanApi.skipInstallment(loan.id, action.installmentId!, remarks); break;
        case 'waive': await loanApi.waiveInstallment(loan.id, action.installmentId!, remarks); break;
      }
      toast.success('Done');
      setAction(null);
      await load();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.response?.data || 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
  if (!loan) return <Box sx={{ p: 3 }}><Typography>Loan not found.</Typography></Box>;

  const isAdmin = !selfService;
  // Rejected / cancelled requests were never disbursed - show a dash rather than an amount that
  // reads as money still owed.
  const neverDisbursed = isNeverDisbursed(loan.status);
  const amountOrDash = (value: number) => (neverDisbursed ? '—' : money(value));

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1} mb={2}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(selfService ? '/loans/my' : '/loans')} sx={{ textTransform: 'none' }}>
          Back
        </Button>
        <Typography variant="h5" fontWeight={700}>{loan.referenceNumber}</Typography>
        <Chip size="small" label={LoanStatusLabels[loan.status]} color={LoanStatusColors[loan.status]} />
      </Stack>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          <Field label="Employee" value={`${loan.employeeName} (${loan.employeeCode})`} />
          <Field label="Type" value={LoanTypeLabels[loan.loanType]} />
          <Field label="Principal" value={money(loan.principalAmount)} />
          <Field label="Interest" value={`${LoanInterestMethodLabels[loan.interestMethod]}${loan.interestRatePercent ? ` @ ${loan.interestRatePercent}%` : ''}`} />
          <Field label="Total Payable" value={amountOrDash(loan.totalPayable)} />
          <Field label="Total Paid" value={amountOrDash(loan.totalPaid)} />
          <Field label="Outstanding" value={amountOrDash(loan.outstandingBalance)} />
          <Field label="Installment" value={amountOrDash(loan.installmentAmount)} />
          <Field label="Installments" value={`${loan.installmentsPaid}/${loan.totalInstallments}`} />
          <Field label="Recovery From" value={`${MONTHS[loan.repaymentStartMonth - 1]} ${loan.repaymentStartYear}`} />
          <Field label="Requested" value={`${loan.requestedBy ?? '—'} · ${fmtDate(loan.requestedOn)}`} />
          <Field label="Disbursed" value={loan.disbursedOn ? `${money(loan.disbursedAmount ?? 0)} · ${fmtDate(loan.disbursedOn)}` : '—'} />
          {loan.purpose && <Field label="Purpose" value={loan.purpose} />}
          {loan.approvalRemarks && <Field label="Approval Remarks" value={loan.approvalRemarks} />}
          {loan.rejectionReason && <Field label="Rejection Reason" value={loan.rejectionReason} />}
          {loan.closureRemarks && <Field label="Closure" value={`${loan.closureRemarks} · ${fmtDate(loan.closedOn)}`} />}
        </Grid>

        {isAdmin && (
          <>
            <Divider sx={{ my: 2 }} />
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {loan.status === LoanStatus.Pending && (
                <>
                  <Button size="small" variant="contained" onClick={() => openAction({ kind: 'approve', title: 'Approve Loan' })}>Approve</Button>
                  <Button size="small" color="error" variant="outlined" onClick={() => openAction({ kind: 'reject', title: 'Reject Loan' })}>Reject</Button>
                  <Button size="small" onClick={() => openAction({ kind: 'cancel', title: 'Cancel Loan' })}>Cancel</Button>
                </>
              )}
              {loan.status === LoanStatus.Approved && (
                <>
                  <Button size="small" variant="contained" onClick={() => openAction({ kind: 'disburse', title: 'Disburse Loan', needsReference: true })}>Disburse</Button>
                  <Button size="small" onClick={() => openAction({ kind: 'cancel', title: 'Cancel Loan' })}>Cancel</Button>
                </>
              )}
              {loan.status === LoanStatus.Active && (
                <>
                  <Button size="small" variant="contained" onClick={() => openAction({ kind: 'repayment', title: 'Record Repayment', needsAmount: true, needsReference: true })}>Record Repayment</Button>
                  <Button size="small" variant="outlined" onClick={() => openAction({ kind: 'foreclose', title: 'Foreclose Loan', needsReference: true })}>Foreclose</Button>
                  <Button size="small" color="error" variant="outlined" onClick={() => openAction({ kind: 'writeoff', title: 'Write Off Loan' })}>Write Off</Button>
                </>
              )}
            </Stack>
          </>
        )}
      </Paper>

      {loan.installments.length > 0 && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Repayment Schedule</Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Due</TableCell>
                  <TableCell align="right">Principal</TableCell>
                  <TableCell align="right">Interest</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell align="right">Paid</TableCell>
                  <TableCell>Status</TableCell>
                  {isAdmin && <TableCell align="right">Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {loan.installments.map((i) => (
                  <TableRow key={i.id}>
                    <TableCell>{i.installmentNumber}</TableCell>
                    <TableCell>{MONTHS[i.dueMonth - 1]} {i.dueYear}</TableCell>
                    <TableCell align="right">{money(i.principalComponent)}</TableCell>
                    <TableCell align="right">{money(i.interestComponent)}</TableCell>
                    <TableCell align="right">{money(i.amount)}</TableCell>
                    <TableCell align="right">{money(i.paidAmount)}</TableCell>
                    <TableCell>{LoanInstallmentStatusLabels[i.status]}</TableCell>
                    {isAdmin && (
                      <TableCell align="right">
                        {loan.status === LoanStatus.Active
                          && (i.status === LoanInstallmentStatus.Pending || i.status === LoanInstallmentStatus.PartiallyRecovered) && (
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                            <Button size="small" onClick={() => openAction({ kind: 'skip', installmentId: i.id, title: `Skip Installment #${i.installmentNumber}` })}>Skip</Button>
                            <Button size="small" color="warning" onClick={() => openAction({ kind: 'waive', installmentId: i.id, title: `Waive Installment #${i.installmentNumber}` })}>Waive</Button>
                          </Stack>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {loan.transactions.length > 0 && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Ledger</Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell align="right">Balance After</TableCell>
                  <TableCell>Reference</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loan.transactions.map((t) => (
                  <TableRow key={t.id}>
                    <TableCell>{fmtDate(t.transactionDate)}</TableCell>
                    <TableCell>{LoanTransactionTypeLabels[t.transactionType]}</TableCell>
                    <TableCell align="right">{money(t.amount)}</TableCell>
                    <TableCell align="right">{money(t.balanceAfter)}</TableCell>
                    <TableCell>{t.reference || t.remarks || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <Dialog open={Boolean(action)} onClose={() => setAction(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{action?.title}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {action?.needsAmount && (
              <TextField
                type="number"
                label="Amount"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                fullWidth
                autoFocus
                error={amountExceedsOutstanding}
                helperText={
                  amountExceedsOutstanding
                    ? `Amount cannot exceed outstanding balance (${money(loan?.outstandingBalance ?? 0)})`
                    : action?.kind === 'repayment'
                      ? `Outstanding balance: ${money(loan?.outstandingBalance ?? 0)}`
                      : undefined
                }
              />
            )}
            {action?.needsReference && (
              <TextField label="Reference (optional)" value={reference} onChange={(e) => setReference(e.target.value)} fullWidth />
            )}
            <TextField label="Remarks" value={remarks} onChange={(e) => setRemarks(e.target.value)} fullWidth multiline minRows={2} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAction(null)} disabled={busy} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={runAction}
            disabled={busy || (action?.needsAmount && !(Number(amount) > 0)) || amountExceedsOutstanding}
            sx={{ textTransform: 'none' }}
          >
            {busy ? 'Working…' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LoanDetailPage;
